/* eslint-disable react/destructuring-assignment */
import React, { memo } from 'react';
import Select, { components } from 'react-select';
import 'react-datepicker/dist/react-datepicker.css';
import { Cross } from '@saleshandy/icons';
import { SelectFilterProps, StepOption } from './type';
import { accessibleOnClick } from '../../../../utils/accessible-on-click';

interface GroupedSteps {
  [key: string]: {
    title: string;
    steps: StepOption[];
  };
}

const HideGroupHeading = (props) => {
  const groupHeadingRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (props?.onScrollEnd && groupHeadingRef.current) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            props.onScrollEnd();
          }
        },
        {
          root: document.querySelector('.select__menu-list'),
          threshold: 1.0,
        },
      );

      observer.observe(groupHeadingRef.current);

      return () => {
        if (groupHeadingRef.current) {
          observer.unobserve(groupHeadingRef.current);
        }
      };
    }

    return () => {};
  }, [props?.onScrollEnd]);

  const handleToggle = () => {
    document
      .querySelector(`#${props.id}`)
      ?.parentElement?.parentElement?.classList.toggle('collapsed-group');
  };

  return (
    <div
      ref={groupHeadingRef}
      className="collapse-group-heading"
      {...accessibleOnClick(handleToggle)}
    >
      <components.GroupHeading {...props} />
    </div>
  );
};

const HideGroupMenuList = (props) => {
  const newProps = {
    ...props,
    children: Array.isArray(props.children)
      ? props.children.map((c, idx) =>
          idx === 0
            ? c
            : { ...c, props: { ...c.props, className: 'collapsed-group' } },
        )
      : props.children,
  };

  return <components.MenuList {...newProps} />;
};

export const CustomMultiValue = (props) => {
  const { data, removeProps } = props;

  return (
    <div className="custom-chip">
      <span className="chip-label">{data.name}</span>
      <Cross width={12} height={12} onClick={removeProps.onClick} />
    </div>
  );
};

/**
 * Shows only `max` MultiValue chips, then a single "+N" for the rest (single-line layout).
 */
const createLimitedMultiValue = (max: number, uniqueKey: string) => (props) => {
  const { index, data, selectProps } = props;
  const { value } = selectProps;
  const count = Array.isArray(value) ? value.length : 0;

  if (count <= max) {
    return <components.MultiValue {...props} />;
  }

  let idx = typeof index === 'number' ? index : -1;
  if (idx < 0 && value?.length && data != null) {
    idx = value.findIndex((v) => v?.[uniqueKey] === data?.[uniqueKey]);
  }

  if (idx < max) {
    return <components.MultiValue {...props} />;
  }

  if (idx === max) {
    const overflowRemoved = count - max;
    const removeOverflowSelections = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const next = value.slice(0, max);
      selectProps.onChange(next, { action: 'remove-value' });
    };

    return (
      <div className="prospects-filter__multi-overflow-count regular-2 gray-txt-9">
        <span>+{overflowRemoved}</span>
        <button
          type="button"
          className="prospects-filter__multi-overflow-clear"
          aria-label="Remove additional selections"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={removeOverflowSelections}
        >
          <Cross width={12} height={12} className="gray-txt-11" />
        </button>
      </div>
    );
  }

  return null;
};

export const CustomValueContainer = (props) => {
  const { getValue, selectProps, children } = props;
  const selected: StepOption[] = getValue();

  const grouped: GroupedSteps = selected.reduce((acc, step) => {
    const id = step.parentSubsequenceId;
    if (!acc[id]) {
      acc[id] = {
        title: step.parentSubsequenceTitle,
        steps: [],
      };
    }
    acc[id].steps.push(step);
    return acc;
  }, {});

  const handleGroupRemove = (groupSteps: StepOption[]) => {
    const updated = selected.filter(
      (step) => !groupSteps.some((g) => g.id === step.id),
    );
    selectProps.onChange(updated, { action: 'remove-value' });
  };

  return (
    <components.ValueContainer {...props}>
      {Object.entries(grouped).map(([parentId, { title, steps }]) => (
        <div key={parentId} className="custom-group">
          <div className="custom-group-header">
            <span className="custom-group-title">{title}</span>

            <Cross
              width={14}
              height={14}
              onClick={(e) => {
                e.stopPropagation();
                handleGroupRemove(steps);
              }}
              color="#4B5563"
            />
          </div>
          <div className="custom-group-steps">
            {steps.map((step) => (
              <div key={step.id} className="custom-chip">
                <span className="chip-label">{step.name}</span>

                <Cross
                  width={12}
                  height={12}
                  onClick={(e) => {
                    e.stopPropagation();
                    selectProps.onChange(
                      selected.filter((s) => s.id !== step.id),
                      { action: 'remove-value' },
                    );
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
      {React.Children.map(children, (child) => {
        if (
          React.isValidElement(child) &&
          'data' in (child as React.ReactElement).props
        ) {
          return null;
        }
        return child;
      })}
    </components.ValueContainer>
  );
};

const MenuList: React.FC<any> = (props) => {
  const observerRef = React.useRef<HTMLDivElement | null>(null);

  const lastOptionIndex = props.children.length - 1;
  React.useEffect(() => {
    if (observerRef.current && props.onScrollEnd) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            props.onScrollEnd();
          }
        },
        {
          root: document.querySelector('.select__menu-list'),
          threshold: 1.0,
        },
      );

      observer.observe(observerRef.current);

      return () => {
        if (observerRef.current) {
          observer.unobserve(observerRef.current);
        }
      };
    }
    return () => {};
  }, [props.onScrollEnd]);

  return (
    <components.MenuList {...props}>
      {React.Children.map(props.children, (child, index) => {
        if (index === lastOptionIndex) {
          return <div ref={observerRef}>{child}</div>;
        }
        return child;
      })}
    </components.MenuList>
  );
};

const Option = (props) => (
  <components.Option {...props} className="d-flex align-items-center">
    <div className="d-flex align-items-center pointer">
      {props?.isMultiSelect && (
        <input
          type="checkbox"
          checked={props.isSelected}
          onChange={() => null}
        />
      )}{' '}
      <span className="ml-2 regular-2 popover-arrow-color-txt">
        {props.children}
      </span>
      {props?.data?.isDeleted && (
        <span className="ml-2 email-filter__deleted-chip">Deleted</span>
      )}
    </div>
  </components.Option>
);

/**
 * Styles to pass in `react-select`
 */
const textStyle = {
  fontSize: '14px',
  fontStyle: 'normal',
  lineHeight: '20px',
  color: '#6b7280',
};

const SelectFilter: React.FC<SelectFilterProps> = ({
  placeholder,
  options,
  labelKey = 'name',
  onSelect,
  uniqueKey = 'id',
  values = [],
  placement = 'bottom',
  isMultiSelect,
  isDisabled = false,
  onScrollEnd,
  useCustomChips = false,
  isGroupCollapsible = false,
  maxVisibleSelectedChips,
  ...rest
}) => {
  const getOptionLabel = (option) => option[labelKey];
  const getOptionValue = (option) => option[uniqueKey];

  const customStyles = {
    control: (base) => ({
      ...base,
      border: '1px solid #E5E7EB',
    }),
    placeholder: (base) => ({
      ...base,
      ...textStyle,
    }),
    option: (base, state) => ({
      ...base,
      ...textStyle,
      color: '#1F2937',
      wordWrap: 'break-word',
      backgroundColor: state.isSelected && '#EFF6FF',
      '&:active': {
        backgroundColor: '#EFF6FF',
      },
      '&:hover': {
        backgroundColor: '#dbeafe',
        color: '#1d4ed8',
      },
    }),
    multiValue: (base) => ({
      ...base,
      marginRight: '8px',
      borderRadius: '4px',
      border: '1px solid #D1D5DB',
      backgroundColor: '#FFF',
    }),
    multiValueLabel: (base) => ({
      ...base,
      ...textStyle,
      fontWeight: 500,
      color: '#1F2937',
    }),
    multiValueRemove: (base) => ({
      ...base,
      marginRight: '0px',
      color: '#6B7280',
      '&:hover': {
        backgroundColor: '#dbeafe',
        color: '#1d4ed8',
      },
    }),
    ...(maxVisibleSelectedChips != null && {
      valueContainer: (base) => ({
        ...base,
        flexWrap: 'nowrap',
        overflow: 'hidden',
      }),
    }),
  };

  return (
    <Select
      value={values}
      isMulti
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      name="colors"
      options={options}
      className="basic-multi-select"
      classNamePrefix="select"
      components={{
        Option: (props) => <Option isMultiSelect={isMultiSelect} {...props} />,
        MenuList: (props) => <MenuList {...props} onScrollEnd={onScrollEnd} />,
        ...(useCustomChips && {
          MultiValue: CustomMultiValue,
          ValueContainer: CustomValueContainer,
        }),
        ...(!useCustomChips &&
          maxVisibleSelectedChips != null &&
          maxVisibleSelectedChips > 0 && {
            MultiValue: createLimitedMultiValue(
              maxVisibleSelectedChips,
              uniqueKey,
            ),
          }),
        ...(isGroupCollapsible && {
          MenuList: HideGroupMenuList,
          GroupHeading: (props) => (
            <HideGroupHeading {...props} onScrollEnd={onScrollEnd} />
          ),
        }),
      }}
      isOptionSelected={(option) =>
        values.filter((op) => op[uniqueKey] === option[uniqueKey]).length > 0
      }
      onChange={onSelect}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      placeholder={placeholder}
      menuPlacement={placement}
      styles={customStyles}
      isDisabled={isDisabled}
      {...rest}
    />
  );
};

export default memo(SelectFilter);
